import { useState } from 'react';

import { AGENDA_MAX_DISCUSSION_POINTS_PER_ITEM } from './agendaLimits';

import type { AgendaDraft } from './agendaDraft';
import type { AgendaItemView, AgendaPointView } from './agendaViewModel';

interface PointRef {
    itemId: string;
    pointId: string;
}

/** A point's title being typed straight in its row, on creation or on rename. */
interface TitleEdit extends PointRef {
    title: string;
    /** Created in its row and never confirmed: cancelling removes the row it left. */
    isNew: boolean;
}

/** The point open in the details modal. */
interface PointDetails {
    itemId: string;
    point: AgendaPointView;
}

const mapPoints = (
    items: AgendaItemView[],
    itemId: string,
    update: (points: AgendaPointView[]) => AgendaPointView[],
) =>
    items.map((item) =>
        item.id === itemId ? { ...item, discussionPoints: update(item.discussionPoints) } : item,
    );

const mapPoint = (
    items: AgendaItemView[],
    { itemId, pointId }: PointRef,
    update: (point: AgendaPointView) => AgendaPointView,
) =>
    mapPoints(items, itemId, (points) =>
        points.map((point) => (point.id === pointId ? update(point) : point)),
    );

const withoutPoint = (items: AgendaItemView[], { itemId, pointId }: PointRef) =>
    mapPoints(items, itemId, (points) => points.filter((point) => point.id !== pointId));

/**
 * The discussion points of the agenda's topics: added and renamed in their row, detailed in a modal,
 * answered, removed and reordered.
 */
export const useAgendaDiscussionPoints = ({
    items,
    setItems,
    commit,
    save,
    editable,
}: AgendaDraft) => {
    const [titleEdit, setTitleEdit] = useState<TitleEdit | null>(null);
    const [details, setDetails] = useState<PointDetails | null>(null);

    const pointCount = (itemId: string) =>
        items.find((item) => item.id === itemId)?.discussionPoints.length ?? 0;

    const canAddTo = (itemId: string) => pointCount(itemId) < AGENDA_MAX_DISCUSSION_POINTS_PER_ITEM;

    const add = (itemId: string) => {
        if (!canAddTo(itemId)) {
            return;
        }

        const point: AgendaPointView = {
            id: crypto.randomUUID(),
            title: '',
            order: pointCount(itemId),
            hasDetails: false,
            context: null,
            dateAdded: new Date().toISOString(),
            response: null,
            isAiGenerated: false,
        };
        // Only local for now: the point is saved once its title is confirmed in its row.
        setItems(mapPoints(items, itemId, (points) => [...points, point]));
        setTitleEdit({ itemId, pointId: point.id, title: '', isNew: true });
    };

    const confirmTitle = () => {
        const title = titleEdit?.title.trim();
        if (!titleEdit || !title) {
            return;
        }

        commit(mapPoint(items, titleEdit, (point) => ({ ...point, title })));
        setTitleEdit(null);
    };

    const cancelTitle = () => {
        if (titleEdit?.isNew) {
            setItems(withoutPoint(items, titleEdit));
        }
        setTitleEdit(null);
    };

    const openDetails = (pointDetails: PointDetails) => {
        setTitleEdit(null);
        setDetails(pointDetails);
    };

    const saveDetails = ({ title, context }: { title: string; context: string | null }) => {
        if (!details) {
            return;
        }

        commit(
            mapPoint(items, { itemId: details.itemId, pointId: details.point.id }, (point) => ({
                ...point,
                title,
                context,
                hasDetails: context != null,
            })),
        );
        setDetails(null);
    };

    return {
        /** False once that topic holds as many discussion points as the backend validator accepts. */
        canAddTo,
        titleEdit,
        details,
        add: editable(add),
        startTitleEdit: editable(({ itemId, point }: PointDetails) =>
            setTitleEdit({ itemId, pointId: point.id, title: point.title, isNew: false }),
        ),
        changeTitle: editable((title: string) =>
            setTitleEdit((current) => current && { ...current, title }),
        ),
        confirmTitle: editable(confirmTitle),
        cancelTitle: editable(cancelTitle),
        openDetails: editable(openDetails),
        saveDetails: editable(saveDetails),
        closeDetails: () => setDetails(null),
        /** Typed without saving: the response is saved when its field is left. */
        changeResponse: editable(({ response, ...ref }: PointRef & { response: string }) =>
            setItems((current) => mapPoint(current, ref, (point) => ({ ...point, response }))),
        ),
        saveResponses: editable(save),
        remove: editable((ref: PointRef) => commit(withoutPoint(items, ref))),
        reorder: editable((itemId: string, points: AgendaPointView[]) =>
            commit(mapPoints(items, itemId, () => points)),
        ),
    };
};
